import React, { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer } from 'reactflow';
import { Trash2, Palette } from 'lucide-react';

const colorOptions = {
  yellow: {
    bg: 'bg-yellow-200',
    border: 'border-yellow-400',
    text: 'text-yellow-900',
    hover: 'hover:bg-yellow-300',
  },
  pink: {
    bg: 'bg-pink-200',
    border: 'border-pink-400',
    text: 'text-pink-900',
    hover: 'hover:bg-pink-300',
  },
  blue: {
    bg: 'bg-blue-200',
    border: 'border-blue-400',
    text: 'text-blue-900',
    hover: 'hover:bg-blue-300',
  },
  green: {
    bg: 'bg-green-200',
    border: 'border-green-400',
    text: 'text-green-900',
    hover: 'hover:bg-green-300',
  },
  orange: {
    bg: 'bg-orange-200',
    border: 'border-orange-400',
    text: 'text-orange-900',
    hover: 'hover:bg-orange-300',
  },
};

export const StickyNoteNode = memo(({ id, data, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(data.content || 'Click to edit...');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const textareaRef = useRef(null);

  const colorStyle = colorOptions[data.color] || colorOptions.yellow;

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (data.onContentChange) {
      data.onContentChange(id, content);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      if (data.onContentChange) {
        data.onContentChange(id, content);
      }
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  const handleColorChange = (color) => {
    if (data.onColorChange) {
      data.onColorChange(id, color);
    }
    setShowColorPicker(false);
  };

  return (
    <>
      <NodeResizer
        minWidth={200}
        minHeight={140}
        isVisible={selected}
        lineClassName="!border-slate-400"
        handleClassName="!w-3 !h-3 !bg-slate-400 !border-2 !border-slate-600"
      />

      <div
        className={`
          relative p-4 rounded shadow-lg min-w-[200px] min-h-[140px]
          ${colorStyle.bg} ${colorStyle.border} border-2
          ${selected ? 'ring-2 ring-slate-400 ring-offset-2 ring-offset-transparent' : ''}
          transition-all duration-200
        `}
        onDoubleClick={handleDoubleClick}
        style={{
          // Slight rotation for sticky note effect
          transform: `rotate(${(parseInt(id.split('-')[1] || '0') % 5) - 2}deg)`,
        }}
      >
        {/* Connection handles - larger hit area for easier connections */}
        <Handle
          type="target"
          position={Position.Top}
          className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-500 hover:!bg-slate-300 hover:!scale-125 transition-transform"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-500 hover:!bg-slate-300 hover:!scale-125 transition-transform"
        />
        <Handle
          type="target"
          position={Position.Left}
          className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-500 hover:!bg-slate-300 hover:!scale-125 transition-transform"
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-500 hover:!bg-slate-300 hover:!scale-125 transition-transform"
        />

        {/* Action buttons */}
        {selected && (
          <div className="absolute -top-8 right-0 flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker(!showColorPicker);
              }}
              className="p-1 rounded bg-slate-700 hover:bg-slate-600 text-white"
            >
              <Palette className="w-3 h-3" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 rounded bg-red-600 hover:bg-red-500 text-white"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Color picker */}
        {showColorPicker && (
          <div className="absolute -top-16 left-0 flex gap-1 p-2 bg-slate-800 rounded-lg shadow-lg border border-slate-600">
            {Object.keys(colorOptions).map((color) => (
              <button
                key={color}
                onClick={(e) => {
                  e.stopPropagation();
                  handleColorChange(color);
                }}
                className={`
                  w-5 h-5 rounded-full border-2 border-white/50
                  ${colorOptions[color].bg}
                  hover:scale-110 transition-transform
                `}
              />
            ))}
          </div>
        )}

        {/* Content */}
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`
              w-full h-full min-h-[100px] resize-none
              bg-transparent border-none outline-none
              ${colorStyle.text} text-base
              font-handwriting
            `}
            style={{ fontFamily: '"Caveat", cursive', fontSize: '18px' }}
          />
        ) : (
          <p
            className={`
              ${colorStyle.text} text-base whitespace-pre-wrap
              cursor-text select-none
            `}
            style={{ fontFamily: '"Caveat", cursive', fontSize: '18px' }}
          >
            {content}
          </p>
        )}

        {/* Pin effect */}
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-red-500 shadow" />
      </div>
    </>
  );
});

StickyNoteNode.displayName = 'StickyNoteNode';

export default StickyNoteNode;
